/**
 * 组件内文案字典（非结构性内容）
 * ---------------------------------------------------------------
 * 结构性内容（产品、新闻、导航、案例…）放在 src/data/site.{en,zh}.js。
 * 这里只收「写死在组件/页面模板里」的界面文案。
 * 键名按 页面 → 区块 组织，便于对照查找。
 */
export const messages = {
  /* ============================ ENGLISH ============================ */
  en: {
    header: {
      megaAutoTitle: 'Auto Parts 汽车配件',
      megaAutoDesc: 'OEM/ODM · Global export',
      megaBuildingTitle: 'Building Materials 建筑材料',
      megaBuildingDesc: 'Factory direct · Container shipping',
      viewAllAuto: 'View All Auto Parts',
      viewAllBuilding: 'View All Building Materials',
      switchTo: 'Switch language'
    },
    footer: {
      quickLinks: 'Quick Links',
      productLines: 'Product Lines',
      contact: 'Get In Touch'
    },
    lesson: {
      what: 'What happens:',
      help: 'How we help:'
    },
    crumbs: {
      home: 'Home'
    },
    home: {
      pillarsKicker: 'Two Business Lines',
      pillarsTitle: 'One Supply Chain. Two Core Industries.',
      pillarsDesc:
        'GUANGZHOU VANCHEEN focus on two pillars where we leverage our deep factory relationships — so you get consistent quality, competitive pricing, and a single point of contact from inquiry to delivery.',
      autoKicker: 'Auto Parts',
      autoTitle: 'Featured Auto Parts Series',
      autoDesc:
        'Precision-machined components for passenger and commercial vehicles — OEM/ODM available, backed by trusted manufacturing partners. Full packaging & marking support.',
      autoCta: 'View All Auto Parts Series',
      buildingKicker: 'Building Materials',
      buildingTitle: 'Featured Building Materials Series',
      buildingDesc:
        'Building materials sourced from trusted manufacturers — with consistent grading, moisture-safe packaging and full container loading plans.',
      buildingCta: 'View All Building Materials Series',
      lessonsKicker: 'Why VANCHEEN',
      lessonsTitle: "Don't Learn These 10 Lessons the Hard Way"
    },
    about: {
      crumb: 'About Us',
      title: 'Who We Are',
      desc:
        'A China-based industrial export supplier connecting global buyers with reliable auto parts and building materials — through deep factory partnerships, quality discipline and logistics strength.',
      valuesKicker: 'What We Stand For',
      valuesTitle: 'Purpose, Direction & Values',
      milestonesKicker: 'Our Journey',
      milestonesTitle: 'Key Milestones'
    },
    products: {
      crumb: 'Products',
      title: 'Our Product Series',
      desc:
        'Two business lines, one accountable supply chain. Filter by industry to explore representative series — every line supports OEM/ODM customization.'
    },
    factory: {
      crumb: 'Factory Tour',
      eyebrow: 'Partner Factory Tour',
      title: 'We Walk the Factory Floor for You.',
      desc:
        'Real photos from the partner factories behind your order — no scripts, no staging.',
      photosKicker: 'Inside the Factory',
      photosTitle: 'Real shots from the factory floor',
      photosDesc: 'Real production lines, real products.',
      visitKicker: 'Arrange a Visit',
      note: 'VANCHEEN — Your sourcing partner. We walk the factory floor for you.'
    },
    quality: {
      crumb: 'Quality Control',
      title: 'Quality Control',
      desc:
        'Quality that survives the journey, not just the factory. A documented 12-step process protects your order from raw material to loading.',
      processKicker: 'The 12-Step Process',
      processTitle: 'How We Protect Your Order',
      certKicker: 'Certifications & Standards',
      certTitle: 'Compliance We Stand Behind',
      certNote:
        'Documentation available for your compliance review: batch inspection reports, material certificates, packing lists and loading photos.'
    },
    news: {
      crumb: 'News & Events',
      title: 'News & Events',
      desc:
        'Exhibitions, factory updates, shipping programs and market insights — follow our global trade activity.'
    }
  },

  /* ============================ 中文 ============================ */
  zh: {
    header: {
      megaAutoTitle: '汽车配件 Auto Parts',
      megaAutoDesc: 'OEM/ODM · 全球出口',
      megaBuildingTitle: '建筑材料 Building Materials',
      megaBuildingDesc: '工厂直供 · 整柜海运',
      viewAllAuto: '查看全部汽车配件',
      viewAllBuilding: '查看全部建筑材料',
      switchTo: '切换语言'
    },
    footer: {
      quickLinks: '快速导航',
      productLines: '产品线',
      contact: '联系我们'
    },
    lesson: {
      what: '常见情形：',
      help: '我们怎么做：'
    },
    crumbs: {
      home: '首页'
    },
    home: {
      pillarsKicker: '两大业务线',
      pillarsTitle: '一条供应链，两个核心行业',
      pillarsDesc:
        'GUANGZHOU VANCHEEN 专注两个我们深耕的支柱品类，凭借与工厂的深度合作关系，为你带来稳定的品质、有竞争力的价格，以及从询价到交付的单一对接窗口。',
      autoKicker: '汽车配件',
      autoTitle: '精选汽车配件系列',
      autoDesc:
        '面向乘用车与商用车的精密加工件——支持 OEM/ODM，依托值得信赖的制造伙伴，并提供完整的包装与唛头方案。',
      autoCta: '查看全部汽车配件系列',
      buildingKicker: '建筑材料',
      buildingTitle: '精选建筑材料系列',
      buildingDesc:
        '来自可信制造商的建筑材料——等级稳定、防潮包装，并提供完整的装柜配载方案。',
      buildingCta: '查看全部建筑材料系列',
      lessonsKicker: '为什么选择 VANCHEEN',
      lessonsTitle: '这 10 个教训，不必用代价去换'
    },
    about: {
      crumb: '关于我们',
      title: '我们是谁',
      desc:
        '一家总部位于中国的工业出口供应商，通过深度的工厂合作、严格的质量管控和强大的物流能力，将全球买家与可靠的汽车零部件及建筑材料连接起来。',
      valuesKicker: '我们的坚持',
      valuesTitle: '使命、方向与价值观',
      milestonesKicker: '发展历程',
      milestonesTitle: '关键里程碑'
    },
    products: {
      crumb: '产品中心',
      title: '我们的产品系列',
      desc:
        '两大业务线，一条负责任的供应链。按行业筛选，查看代表性系列——每条产线均支持 OEM/ODM 定制。'
    },
    factory: {
      crumb: '工厂参观',
      eyebrow: '合作工厂实地实拍',
      title: '我们替您走进工厂。',
      desc: '这些都是你订单背后合作工厂的真实照片——没有脚本，没有摆拍。',
      photosKicker: '走进工厂',
      photosTitle: '来自车间的真实画面',
      photosDesc: '真实的生产线，真实的产品。',
      visitKicker: '安排参观',
      note: 'VANCHEEN——你的采购伙伴。我们替您走进工厂。'
    },
    quality: {
      crumb: '品质控制',
      title: '品质控制',
      desc:
        '品质要经得起运输，而不只是经得起出厂。一套有据可查的 12 步流程，从原材料到装柜全程守护你的订单。',
      processKicker: '12 步品控流程',
      processTitle: '我们如何守护你的订单',
      certKicker: '认证与标准',
      certTitle: '我们背书的合规资质',
      certNote:
        '以下资料可供你合规审核：批次检验报告、材质证明、装箱单与装柜照片。'
    },
    news: {
      crumb: '新闻资讯',
      title: '新闻与展会',
      desc: '展会动态、工厂进展、发运计划与市场洞察——关注我们的全球贸易足迹。'
    }
  }
}
